import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router-dom";
import { useFlowActions, useFlowState } from "../../state/FlowProvider";
import { clearFlowState, saveFlowState } from "../../state/persistence";
import { onboardingTokens } from "../../theme";
import { t } from "../../../../locales/t";
import { clearOnboardingTokens, finishOnboarding } from "../../api";
import { buildCompletionPayload } from "../../completionPayload";
import { ONBOARDING_BASE_PATH, previousStep } from "../registry";
import { dashboardTabPath } from "../../../../hooks/useDashboardActiveTab";
import GoalPills from "../../components/Done/GoalPills";
import NextStepCard from "../../components/Done/NextStepCard";
import AppDownloadSection from "../../components/Done/AppDownloadSection";

const headingSx = { fontSize: "clamp(1.65rem, 5vw, 2.35rem)", mb: 1.5 };

/** Goals served by the Learn library; the rest are served by the mobile app. */
const LEARN_GOALS = ["build_skills", "grow_career"];

/**
 * The end of the flow. Completion is sent as soon as this renders, so a member
 * who leaves from here for the mobile app has already saved everything.
 *
 * Success marks the flow completed, which ends persistence, but keeps the
 * in-memory flow so this screen goes on rendering. Resetting the flow here
 * would send the guard back to welcome and on to the dashboard.
 *
 * A refusal is read by class, because the two classes need opposite things.
 * Anything in the 4xx range is about the payload, and no number of retries
 * changes the payload, so the flow reopens and the member is put back on the
 * last step they can edit. Anything else may succeed unchanged, so the retry
 * stays. Arriving here seals the flow behind `submitted`, and the 4xx branch is
 * what keeps that seal from outliving a completion that never happened.
 */
const DoneStep = () => {
  const state = useFlowState();
  const { setSubmitted, setCompleted } = useFlowActions();
  const navigate = useNavigate();
  const [status, setStatus] = useState("saving");
  const mounted = useRef(false);
  const headingRef = useRef(null);

  const complete = () =>
    finishOnboarding(buildCompletionPayload(state)).then(
      () => {
        // Guarded for the same reason the refusal below is: a request that
        // outlived this screen must not tear down whatever is mounted now. The
        // app session is installed either way, inside `finishOnboarding`.
        if (!mounted.current) return;

        setCompleted();
        setStatus("ready");
      },
      (error) => {
        // A request that outlived this screen must not navigate or clear the
        // session of whatever is mounted now.
        if (!mounted.current) return;

        console.error("Onboarding completion failed:", error);
        if (error?.status === 401) {
          // The entered flow stays, so signing back in resumes it, and that
          // resume belongs at demographics, so the flow is unsent. Straight to
          // storage because navigating away unmounts the provider before its
          // persist effect could run, the same reason `reset()` writes through.
          saveFlowState({ ...state, submitted: false });
          clearOnboardingTokens();
          navigate("/login", {
            replace: true,
            state: { sessionExpired: true },
          });
          return;
        }
        if (error?.status === 409) {
          clearOnboardingTokens();
          clearFlowState();
          navigate("/login", { replace: true });
          return;
        }

        const back =
          error?.status >= 400 && error.status < 500
            ? previousStep("done", state)
            : null;

        if (back) {
          // Refused over the answers themselves, so the flow is unsent after
          // all and reopens. Written through rather than dispatched because the
          // navigation below unmounts the provider first; the remount rehydrates
          // from storage, which is what the 401 branch above relies on too.
          saveFlowState({ ...state, submitted: false });
          // `replace` so Done leaves history: a Back onto it would send the
          // same rejected payload again.
          navigate(`${ONBOARDING_BASE_PATH}/${back.path}`, {
            replace: true,
            state: { completionFailed: true },
          });
          return;
        }

        setStatus("failed");
      },
    );

  const retry = () => {
    setStatus("saving");
    complete();
  };

  useEffect(() => {
    mounted.current = true;
    // Arriving here is what sends the flow, so the guard can refuse to reopen
    // the answers behind it.
    setSubmitted(true);
    complete();
    return () => {
      mounted.current = false;
    };
    // Mount only: both StrictMode runs share one request via `finishOnboarding`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (status === "ready") headingRef.current?.focus();
  }, [status]);

  const goals = state.goals ?? [];
  const offersLearn = goals.some((goal) => LEARN_GOALS.includes(goal));

  return (
    <Box sx={{ textAlign: "center" }} data-completion={status}>
      {status === "saving" && (
        <Box role="status">
          <CircularProgress
            aria-hidden="true"
            sx={{ color: onboardingTokens.teal, mb: 3 }}
          />
          <Typography variant="h1" sx={headingSx}>
            {t("onboarding.done.saving")}
          </Typography>
          <Typography sx={{ color: onboardingTokens.mutedForeground }}>
            {t("onboarding.done.savingBody")}
          </Typography>
        </Box>
      )}

      {status === "failed" && (
        <>
          <Alert severity="error" sx={{ mb: 2, textAlign: "left" }}>
            {t("onboarding.done.failed")}
          </Alert>
          <Button
            variant="contained"
            fullWidth
            onClick={retry}
            sx={{ py: 1.5 }}
          >
            {t("onboarding.done.retry")}
          </Button>
        </>
      )}

      {status === "ready" && (
        <>
          <CheckCircleOutlineIcon
            sx={{ fontSize: 72, color: onboardingTokens.teal, mb: 2 }}
          />
          <Typography
            ref={headingRef}
            tabIndex={-1}
            variant="h1"
            sx={{ ...headingSx, outline: "none" }}
          >
            {t("onboarding.done.heading")}
          </Typography>
          <Typography sx={{ color: onboardingTokens.mutedForeground, mb: 1 }}>
            {t(`onboarding.done.${state.pathway}`)}
          </Typography>
          <Typography sx={{ color: onboardingTokens.mutedForeground, mb: 3 }}>
            {t("onboarding.done.body")}
          </Typography>

          <GoalPills goals={goals} />

          {offersLearn && (
            <NextStepCard
              to={dashboardTabPath("learn")}
              title={t("onboarding.done.nextSteps.learn.title")}
              cta={t("onboarding.done.nextSteps.learn.cta")}
            />
          )}

          <AppDownloadSection />

          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate("/dashboard", { replace: true })}
            sx={{ py: 1.5 }}
          >
            {t("onboarding.done.enter")}
          </Button>
        </>
      )}
    </Box>
  );
};

export default DoneStep;
